/* Auriani v7 collection page (step 2B): live filters, sort, crawlable Load more with Back restore,
   folded intro on mobile. Uses Shopify's Section Rendering API; no libraries. */
(() => {
  if (window.auCollection) return;
  window.auCollection = true;
  const KEEP = ['preview_theme_id', '_fd'];
  const STORE = 'au-col-list:v1';
  const root = () => document.querySelector('[data-au-col]');
  const keep = url => {
    const here = new URLSearchParams(location.search);
    KEEP.forEach(key => { if (here.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, here.get(key)); });
    return url;
  };
  const say = text => { const live = root()?.querySelector('[data-au-live]'); if (live) live.textContent = text; };
  let pending = null;
  let ticket = 0;

  async function fetchSection(target) {
    const current = root();
    if (!current) return null;
    const url = keep(new URL(target, location.origin));
    if (url.origin !== location.origin) return null;
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', current.dataset.sectionId);
    endpoint.hash = '';
    pending?.abort();
    pending = new AbortController();
    const mine = ++ticket;
    const timer = setTimeout(() => pending?.abort(), 12000);
    try {
      const response = await fetch(endpoint, { credentials: 'same-origin', signal: pending.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (mine !== ticket) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-au-col]');
      if (!fresh) throw new Error('missing section');
      return { url, fresh };
    } finally {
      clearTimeout(timer);
    }
  }

  const cleanUrl = url => {
    const out = new URL(url);
    out.searchParams.delete('section_id');
    return out;
  };

  /* Full swap (sort, chip removal, clear all, Back/Forward). */
  async function navigate(target, mode = 'push') {
    const current = root();
    if (!current) return;
    current.setAttribute('aria-busy', 'true');
    try {
      const result = await fetchSection(target);
      if (!result) return;
      current.querySelector('dialog[open]')?.close();
      current.replaceChildren(...[...result.fresh.childNodes].map(node => document.importNode(node, true)));
      const url = cleanUrl(result.url);
      if (mode === 'push') history.pushState({ auCol: true }, '', url);
      if (mode !== 'pop') clearList();
      init();
      const count = current.querySelector('[data-au-count]')?.textContent || '';
      say(`${count} pieces`);
      if (url.hash === '#products' || mode === 'push') {
        const top = current.querySelector('[data-au-toolbar]');
        if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ block: 'start' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') location.href = String(target);
    } finally {
      current.removeAttribute('aria-busy');
    }
  }

  /* Live update while the filter drawer is open: results, counts and the toolbar, not the drawer. */
  let firstChange = true;
  async function refresh(form) {
    const current = root();
    const url = new URL(form.action, location.origin);
    for (const [key, value] of new FormData(form)) {
      if (key.startsWith('au-') || String(value).trim() === '') continue;
      url.searchParams.append(key, value);
    }
    const status = current.querySelector('[data-au-status]');
    if (status) status.textContent = 'Updating results';
    current.querySelector('[data-au-results]')?.setAttribute('aria-busy', 'true');
    try {
      const result = await fetchSection(url);
      if (!result) return;
      const { fresh } = result;
      const swap = selector => {
        const a = current.querySelector(selector);
        const b = fresh.querySelector(selector);
        if (a && b) a.replaceWith(document.importNode(b, true));
      };
      swap('[data-au-results]');
      swap('[data-au-active]');
      swap('[data-au-sort-list]');
      const count = fresh.querySelector('[data-au-count]')?.textContent || '0';
      current.querySelectorAll('[data-au-count],[data-au-total]').forEach(node => { node.textContent = count; });
      current.querySelectorAll('[data-au-count-for]').forEach(node => {
        const next = fresh.querySelector(`[data-au-count-for="${node.dataset.auCountFor}"]`);
        const value = next ? next.textContent : '0';
        node.textContent = value;
        const row = node.closest('[data-au-row]');
        const control = row?.querySelector('[data-au-group]');
        const empty = value === '0' && !control?.checked;
        row?.classList.toggle('is-empty', empty);
        if (control) control.disabled = empty;
      });
      const clear = current.querySelector('[data-au-clear]');
      const freshClear = fresh.querySelector('[data-au-clear]');
      if (clear && freshClear) clear.hidden = freshClear.hidden;
      const clean = cleanUrl(result.url);
      if (firstChange) history.pushState({ auCol: true }, '', clean); else history.replaceState({ auCol: true }, '', clean);
      firstChange = false;
      clearList();
      if (status) status.textContent = `${count} pieces`;
    } catch (error) {
      if (error.name !== 'AbortError' && status) status.textContent = 'Results could not be updated. Please try again.';
    } finally {
      current.querySelector('[data-au-results]')?.removeAttribute('aria-busy');
    }
  }

  /* Load more: append the next page, update the URL, remember the list for Back. */
  async function loadMore(link) {
    const current = root();
    link.setAttribute('aria-busy', 'true');
    try {
      const result = await fetchSection(link.href);
      if (!result) return;
      const grid = current.querySelector('[data-au-grid]');
      const freshGrid = result.fresh.querySelector('[data-au-grid]');
      const before = grid ? grid.children.length : 0;
      if (grid && freshGrid) grid.append(...[...freshGrid.children].map(node => document.importNode(node, true)));
      const wrap = current.querySelector('[data-au-more-wrap]');
      const freshWrap = result.fresh.querySelector('[data-au-more-wrap]');
      if (wrap && freshWrap) wrap.replaceWith(document.importNode(freshWrap, true));
      const clean = cleanUrl(result.url);
      history.replaceState({ auCol: true, list: true }, '', clean);
      saveList();
      const added = grid ? grid.children.length - before : 0;
      say(`${added} more pieces loaded`);
      grid?.children[before]?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') location.href = link.href;
    }
  }

  function saveList() {
    const current = root();
    const grid = current?.querySelector('[data-au-grid]');
    const wrap = current?.querySelector('[data-au-more-wrap]');
    if (!grid || !wrap) return;
    try {
      sessionStorage.setItem(STORE, JSON.stringify({ url: location.pathname + location.search, grid: grid.innerHTML, wrap: wrap.outerHTML, y: scrollY }));
    } catch (error) { /* storage full or blocked: Back shows the last page only */ }
  }
  function clearList() {
    try { sessionStorage.removeItem(STORE); } catch (error) { /* ignore */ }
  }
  function restoreList() {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch (error) { saved = null; }
    if (!saved || saved.url !== location.pathname + location.search) return false;
    const current = root();
    const grid = current?.querySelector('[data-au-grid]');
    const wrap = current?.querySelector('[data-au-more-wrap]');
    if (!grid || !wrap) return false;
    grid.innerHTML = saved.grid;
    const holder = document.createElement('div');
    holder.innerHTML = saved.wrap;
    if (holder.firstElementChild) wrap.replaceWith(holder.firstElementChild);
    current.querySelector('.au-col__prev')?.remove();
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const target = saved.y || 0;
    let tries = 0;
    const apply = () => {
      scrollTo(0, target);
      if (Math.abs(scrollY - target) > 40 && tries++ < 12) setTimeout(apply, 150);
    };
    requestAnimationFrame(apply);
    addEventListener('load', () => { if (Math.abs(scrollY - target) > 40) scrollTo(0, target); }, { once: true });
    return true;
  }

  function closeSort(except) {
    document.querySelectorAll('[data-au-sort-btn][aria-expanded="true"]').forEach(button => {
      if (button === except) return;
      button.setAttribute('aria-expanded', 'false');
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (list) list.hidden = true;
    });
  }

  function init() {
    const current = root();
    if (!current) return;
    firstChange = true;
    current.querySelectorAll('[data-au-fold]').forEach(box => {
      const button = box.parentElement.querySelector('[data-au-fold-btn]');
      if (!button) return;
      button.hidden = !(box.scrollHeight > box.clientHeight + 2);
    });
  }

  document.addEventListener('click', event => {
    const current = root();
    if (!current) return;
    const sortButton = event.target.closest('[data-au-sort-btn]');
    if (sortButton && current.contains(sortButton)) {
      const open = sortButton.getAttribute('aria-expanded') !== 'true';
      closeSort(sortButton);
      sortButton.setAttribute('aria-expanded', String(open));
      const list = document.getElementById(sortButton.getAttribute('aria-controls'));
      if (list) {
        list.hidden = !open;
        if (open) (list.querySelector('[aria-current]') || list.querySelector('a'))?.focus();
      }
      return;
    }
    if (!event.target.closest('[data-au-sort]')) closeSort();
    const fold = event.target.closest('[data-au-fold-btn]');
    if (fold && current.contains(fold)) {
      const box = document.getElementById(fold.getAttribute('aria-controls'));
      const open = fold.getAttribute('aria-expanded') !== 'true';
      fold.setAttribute('aria-expanded', String(open));
      box?.classList.toggle('is-open', open);
      fold.textContent = open ? 'Read less' : 'Read more';
      return;
    }
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const more = event.target.closest('[data-au-more]');
    if (more && current.contains(more)) {
      event.preventDefault();
      loadMore(more);
      return;
    }
    const clear = event.target.closest('[data-au-clear]');
    if (clear && current.contains(clear)) {
      event.preventDefault();
      const form = current.querySelector('[data-au-form]');
      if (!form) return;
      form.querySelectorAll('[data-au-member],[data-au-group][type="checkbox"]').forEach(input => { input.checked = false; });
      form.querySelectorAll('[data-au-all]').forEach(input => { input.checked = true; });
      form.querySelectorAll('[data-au-pricef]').forEach(input => { input.value = ''; });
      refresh(form);
      return;
    }
    const link = event.target.closest('[data-au-nav]');
    if (link && current.contains(link) && new URL(link.href).origin === location.origin) {
      event.preventDefault();
      closeSort();
      navigate(link.href);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-au-sort-btn][aria-expanded="true"]');
    if (!open) return;
    closeSort();
    open.focus();
  });

  let priceTimer = 0;
  document.addEventListener('change', event => {
    const form = event.target.closest('[data-au-form]');
    if (!form || !root()?.contains(form)) return;
    const input = event.target;
    if (input.matches('[data-au-group][type="checkbox"]')) {
      form.querySelectorAll(`[data-au-member="${input.dataset.auGroup}"]`).forEach(member => { member.checked = input.checked; });
    } else if (input.matches('input[type="radio"]')) {
      const list = input.closest('fieldset');
      list?.querySelectorAll('[data-au-member]').forEach(member => { member.checked = member.dataset.auMember === input.dataset.auGroup; });
    } else if (input.matches('[data-au-pricef]')) {
      clearTimeout(priceTimer);
      const fields = [...form.querySelectorAll('[data-au-pricef]')];
      if (fields.length === 2 && fields[0].value !== '' && fields[1].value !== '' && Number(fields[0].value) > Number(fields[1].value)) {
        fields[1].setCustomValidity('The "to" price must be higher than the "from" price.');
        fields[1].reportValidity();
        return;
      }
      fields.forEach(field => field.setCustomValidity(''));
      priceTimer = setTimeout(() => refresh(form), 350);
      return;
    } else {
      return;
    }
    refresh(form);
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-au-form]');
    if (!form) return;
    event.preventDefault();
    refresh(form);
  });

  /* Remember the scroll position for Back while a loaded-more list is shown. */
  let scrollTimer = 0;
  addEventListener('scroll', () => {
    if (!history.state?.list) return;
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(saveList, 200);
  }, { passive: true });
  addEventListener('pagehide', () => { if (history.state?.list) saveList(); });

  addEventListener('popstate', event => {
    if (!root()) return;
    if (event.state?.auCol) navigate(location.href, 'pop');
  });

  const start = () => {
    if (!root()) return;
    if (history.state?.list) restoreList();
    else history.replaceState({ ...(history.state || {}), auCol: true }, '');
    init();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
